/**
 * Card Visibility Utilities
 * 
 * Functions for calculating how many cards are visible in columns
 * and how many are hidden below the fold. Used during column drag
 * to freeze counts and prevent UI flickering.
 */

const CARD_GAP = 14; // Gap between cards (from CSS)

/**
 * Calculate how many cards would fit from the top of a column
 * Used to limit rendered cards during column drag for performance
 * @returns {number} Number of visible cards
 */
export const calculateVisibleCardCount = () => {
  // Find a column's cards container to measure
  const cardsContainer = document.querySelector('.kanban-column__cards');
  if (!cardsContainer) return 6; // Fallback
  
  // Get the available height of the cards container
  const containerHeight = cardsContainer.clientHeight;
  
  // Get all cards and measure their heights from top down
  const cards = cardsContainer.querySelectorAll('.sortable-card-wrapper');
  if (!cards.length) return 6; // Fallback
  
  let accumulatedHeight = 0;
  let count = 0;
  
  for (const card of cards) {
    const cardHeight = card.offsetHeight;
    
    // Add gap for all cards except the first
    if (count > 0) {
      accumulatedHeight += CARD_GAP;
    }
    
    accumulatedHeight += cardHeight;
    
    // If this card would exceed the container height, stop
    if (accumulatedHeight > containerHeight) {
      break;
    }
    
    count++;
  }
  
  // Add 1 buffer for partial visibility at bottom
  return Math.max(count + 1, 3); // At least 3
};

/**
 * Calculate hidden card counts for all columns
 * Returns an object mapping column IDs to their hidden card count
 * @returns {Object<string, number>} Map of columnId -> hidden count
 */
export const calculateHiddenCounts = () => {
  const counts = {};
  const columns = document.querySelectorAll('.kanban-column');
  
  columns.forEach(column => {
    const columnId = column.dataset.columnId;
    if (!columnId) return;
    
    const cardsContainer = column.querySelector('.kanban-column__cards');
    if (!cardsContainer) {
      counts[columnId] = 0;
      return;
    }
    
    const hasScroll = cardsContainer.scrollHeight > cardsContainer.clientHeight;
    const isNotAtBottom = cardsContainer.scrollHeight - cardsContainer.scrollTop - cardsContainer.clientHeight > 20;
    
    if (hasScroll && isNotAtBottom) {
      const cards = cardsContainer.querySelectorAll('.sortable-card-wrapper');
      const containerBottom = cardsContainer.getBoundingClientRect().bottom;
      let hidden = 0;
      
      cards.forEach(card => {
        if (card.getBoundingClientRect().top > containerBottom) {
          hidden++;
        }
      });
      counts[columnId] = hidden;
    } else {
      counts[columnId] = 0;
    }
  });
  
  return counts;
};

